import React from 'react'
import PropTypes from 'prop-types'
import { cx } from '@emotion/css'

import PinataLink from '../../internal/PinataLink'
import { specialKeyPressed } from '../../helpers/eventHelpers'
import styles from './styles'
import buttonStyles, { buttonVariant, buttonSize } from '../Button/styles'

/**
 * Renders a hyperlink as a button.
 * The hyperlink can be a standard anchor tag or a router Link based on the href and external props.
 * The hyperlink is styled based on the provided theme, size, and variant.
 *
 * @param {Object} props - The properties that define the HyperButton component.
 * @param {string} props.className - The CSS class name of the HyperButton.
 * @param {string} props.size - The size for the HyperButton.
 * @param {string} props.variant - The variant for the HyperButton. Default is 'default'.
 * @param {string} props.theme - The theme for the HyperButton.
 * @param {string} props.href - The href for the Link or anchor tag.
 * @param {string} props.rel - The rel attribute for the Link or anchor tag.
 * @param {string} props.target - The target for the Link or anchor tag.
 * @param {string} props.download - The download attribute for the Link or anchor tag.
 * @param {string} props.label - The label for the HyperButton.
 * @param {function} props.onClick - The function to be called when the HyperButton is clicked.
 * @param {boolean} props.external - Whether the hyperlink should be an external link.
 * @param {function|Object} props.as - The routing component or object to be used when needed - eg react-router-dom Link.
 * @returns {ReactElement} The HyperButton component.
 */
const HyperButton = ({ className, size, variant = 'default', theme, href, rel, target, download, label, onClick, external, as }) => {
  const Component = href && !external ? PinataLink : 'a'
  const componentProps = {
    ...(href
      ? {
          ...(external
            ? {
                href,
                rel,
              }
            : {
                to: href,
                as,
              }),
          target,
          download,
          onClick,
        }
      : {
          onClick,
          onKeyDown: ev => {
            if (ev.which === 13 && !specialKeyPressed(ev) && onClick) onClick(ev)
          },
          tabIndex: '0',
        }),
  }

  return (
    <Component
      className={cx(buttonStyles.button, styles.link, className, buttonSize(size), buttonVariant(variant, theme))}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...componentProps}>
      {label}
    </Component>
  )
}

HyperButton.propTypes = {
  label: PropTypes.string.isRequired,
  href: PropTypes.string,
  target: PropTypes.oneOf(['_self', '_blank', '_parent', '_top']),
  theme: PropTypes.oneOf(['', 'primary', 'secondary', 'success', 'warning', 'error', 'legacy', 'light', 'tertiary', 'info']),
  size: PropTypes.oneOf(['', 'default', 'small', 'medium']),
  variant: PropTypes.oneOf(['', 'default', 'outlined', 'dashed']),
  onClick: PropTypes.func,
  download: PropTypes.string,
  external: PropTypes.bool,
  rel: PropTypes.string,
  className: PropTypes.string,
  as: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({})]),
}

HyperButton.defaultProps = {
  theme: 'primary',
  size: 'default',
  external: false,
}

export default HyperButton
